import { clientSessionViewStore } from '../../services/clientSessionViewStore.js'
import {
  ensureSessionCapability,
  SESSION_HEADER,
} from '../core/session/session-identity.js'
import type { SessionProjectionEnvelopeV1 } from '../core/session/session-projection.js'
import { acceptSessionProjectionEnvelope } from './accept-session-projection-response.js'

export interface SessionLocationInput {
  latitude: number
  longitude: number
  city?: string
  country?: string
}

async function refreshProjection(sessionId: string): Promise<void> {
  await ensureSessionCapability(sessionId)
  const response = await fetch('/api/session', {
    method: 'GET',
    credentials: 'same-origin',
    headers: { [SESSION_HEADER]: sessionId },
    cache: 'no-store',
  })
  if (!response.ok) return
  acceptSessionProjectionEnvelope(await response.json(), '/api/session:location-refresh')
}

/**
 * Commit browser geolocation as an explicit coordinator command.
 * The browser may display the coordinates optimistically, but only the returned
 * projection envelope becomes session truth.
 */
export async function submitSessionLocation(
  sessionId: string,
  location: SessionLocationInput
): Promise<SessionProjectionEnvelopeV1> {
  await ensureSessionCapability(sessionId)
  const commandId = globalThis.crypto.randomUUID()

  for (let attempt = 0; attempt < 2; attempt += 1) {
    const current = clientSessionViewStore.getServerProjectionSnapshot()
    if (!current || current.sessionId !== sessionId) {
      await refreshProjection(sessionId)
    }
    const envelope = clientSessionViewStore.getServerProjectionSnapshot()
    if (!envelope || envelope.sessionId !== sessionId) {
      throw new Error('Canonical session projection is unavailable')
    }

    const response = await fetch('/api/session', {
      method: 'PATCH',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        [SESSION_HEADER]: sessionId,
      },
      body: JSON.stringify({
        sessionId,
        commandId,
        expectedVersion: envelope.version,
        location,
      }),
    })

    if (response.status === 409 && attempt === 0) {
      await refreshProjection(sessionId)
      continue
    }

    const payload: unknown = await response.json()
    if (!response.ok) {
      const message =
        payload && typeof payload === 'object' && typeof (payload as { error?: unknown }).error === 'string'
          ? (payload as { error: string }).error
          : `Location update failed: ${response.status}`
      throw new Error(message)
    }
    if (!acceptSessionProjectionEnvelope(payload, '/api/session:location-update')) {
      throw new Error('Location update returned an invalid projection envelope')
    }

    const accepted = clientSessionViewStore.getServerProjectionSnapshot()
    if (!accepted || accepted.sessionId !== sessionId) {
      throw new Error('Location projection was not accepted')
    }
    return accepted
  }

  throw new Error('Location update conflicted with a newer session version')
}
